// Imports
import { faComment } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useMatch, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import LinkButton from '../../../../../../shareables/foundation/front-end/components/buttons/link';
import Editor from '../../../../../../shareables/foundation/front-end/components/forms/fields/editor';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Select from '../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import MultiFieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import { visuallyHiddenLabelStyle } from '../../../../shared-components/visually-hidden-label';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import usePagination from '../../../../../../shareables/foundation/front-end/utils/use-pagination';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import {
	UploadImageParameters,
	UploadImageFiles,
	UploadImageResponse,
} from '../../../../../../shareables/types/api/main/website/editor/image/upload';
import { PostTopicParameters } from '../../../../../../shareables/types/api/main/website/page/type/215/topic/post';
import { GetTopicsResponse } from '../../../../../../shareables/types/api/main/website/page/type/215/topics/get';
import parseAndSanitizeMarkdown from '../../../../../../shareables/utils/markdown/parse-and-sanitize';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import Byline from '../shared-components/byline';
import Panel from '../shared-components/panel';
import PostLink from '../shared-components/post-link';
import { StyledEditorGeneratedContentContainer } from '../shared-components/styled-editor-generated-content-container';
import UserControlButton from '../shared-components/user-control-button';


// Styled components
const FormContainer = styled.div`
	min-width: 0;
	
	.ck.ck-editor__editable_inline {
		ul,
		ol {
			margin-left: 2em;
		}
	}
`;

const SearchAndPageSizeWrapper = styled(MultiFieldContainer)`
	label {
		${visuallyHiddenLabelStyle}
	}
	
	margin-bottom: 0.8438rem;
`;

const ResultsHeading = styled.h3`
	display: block;
	font-size: 1.5rem;
	margin-top: 1.25rem;
	margin-bottom: 0.5313rem;
	font-weight: 500;
	line-height: 1.1;
`;

const Topic = styled.div`
	background: #fafafa;
	border: 0.0625rem solid #eee;
	border-radius: 0.1875rem;
	padding: 1rem;
	
	&:not(:last-child) {
		margin-bottom: 1rem;
	}
`;

const Title = styled.h3`
	display: inline-block;
	margin-bottom: 0.25rem;
	line-height: 1;
	font-weight: 400;
	font-size: 1.4rem;
	overflow-wrap: anywhere;
`;

const TopicContent = styled(StyledEditorGeneratedContentContainer)`
	margin-top: 0.75rem;
	margin-bottom: 0.75rem;
	display: -webkit-box;
	overflow-y: hidden;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow-wrap: anywhere;
	max-height: 4rem;
	width: 100%;
	
	img {
		max-width: 100%;
	}
	
	p:last-child {
		margin-bottom: 0;
	}
`;

const Comments = styled(LinkButton)<{ theme: GeneratedThemeProps }>`
	color: inherit !important;
	background-color: hsl(0, 0%, 89%) !important;
	${(props) =>
		(props.theme.isPebbles || props.theme.isPaper) &&
		css`
			&& {
				text-decoration: none;
			}
		`}
	
	&:hover,
	&:focus {
		background-color: hsl(0, 0%, 84%) !important;
	}
	
	.svg-inline--fa {
		margin-right: 0.25rem;
	}
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookTopics?: GetTopicsResponse;
	storybookSearchQuery?: string;
}


// Function component
const Forum: React.FC<Props> = ({ pageID, storybookTopics, storybookSearchQuery }) => {
	// Use state
	const [allTopics, setAllTopics] = useState<GetTopicsResponse | null>(storybookTopics ?? null);
	const [formKey, setFormKey] = useState(0);
	
	
	// Use navigation
	const navigate = useNavigate();
	const isPanelOpen = useMatch('/p/:partialURI/create') !== null;
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const member = useAppSelector((state) => state.self.member);
	
	
	// Create search field
	const {
		items: filteredTopics,
		setItems: setFilteredTopics,
		searchQuery,
		searchField,
		pageSizeField,
		paginationComponent,
	} = usePagination<GetTopicsResponse[0]>({
		cacheStorageKey: `${pageID ?? ''}-topics`,
		pluralDescriptor: 'topics',
		initialItems: storybookTopics,
		initialSearchQuery: storybookSearchQuery,
		pageSizes: [10, 25, 50, 100],
		searchableKeys: ['title', 'text', 'submitter.formattedName', 'posted'],
		missingSearchableKeyFallbacks: {
			'submitter.formattedName': 'Deleted user',
		},
		pageNavigationProps: {
			addBorders: true,
		},
	});
	
	
	// Function that loads topics
	const memoizedLoadTopics = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetTopicsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/215/topics`,
				completion: (json) => {
					setAllTopics(json.response);
					setFilteredTopics(json.response);
				},
			})
		);
	}, [dispatch, pageID, setFilteredTopics]);
	
	
	// Load topics on mount
	useEffect(() => {
		memoizedLoadTopics();
	}, [memoizedLoadTopics]);
	
	
	// Reset form when panel closes
	useEffect(() => {
		if (isPanelOpen) {
			return;
		}
		
		setFormKey((previousKey) => previousKey + 1);
	}, [isPanelOpen]);
	
	
	// If loading, then display nothing
	if (allTopics === null || filteredTopics === null) {
		return null;
	}
	
	
	// Add placeholder to search field
	const searchFieldWithPlaceholder = React.cloneElement(searchField, { placeholder: 'Search forum…' });
	
	
	// Build form options
	const formOptions: APIFormOptions<PostTopicParameters, undefined, undefined> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/215/topic`,
		submitButton: {
			text: 'Create',
		},
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Display page message
		dispatch(
			commonActions.showPageMessage({
				title: 'Success!',
				message: 'Your topic has been created.',
				color: 'success',
			})
		);
		
		
		// Reload topics
		memoizedLoadTopics();
		
		
		// Close panel
		navigate('..', { relative: 'path', replace: true });
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<UserControlButton
				text='Create a new topic'
				opened={isPanelOpen}
				onClick={() => navigate('create', { replace: true })}
				icon={faPlus}
			/>
			
			<AnimateHeight height={isPanelOpen ? 'auto' : 0} duration={400} animateOpacity>
				<Panel closeOnClick={() => navigate('..', { relative: 'path', replace: true })} signInText='creating a topic'>
					<FormContainer>
						<APIForm
							options={formOptions}
							onCancel={() => navigate('..', { relative: 'path', replace: true })}
							onCompletion={handleFormCompletion}
							key={formKey}
						>
							<Input
								type='text'
								name='title'
								label='Title'
								maxLength={40}
								placeholder='Something short and to the point'
								required
							/>
							
							<Editor
								name='text'
								label='Your comment'
								handleUpload={(file) =>
									new Promise((resolve, reject) => {
										void dispatch(
											apiActions.call<UploadImageParameters, UploadImageFiles, UploadImageResponse>({
												action: 'UPLOAD',
												uri: `/website/${window.location.hostname}/editor/image`,
												parameters: {
													pageID,
												},
												files: {
													image: file,
												},
												completion: (json) => {
													if (json.status === 'error') {
														reject(json.message);
														return;
													}
													
													resolve(json.response.url);
												},
												handleErrorsAutomatically: false,
											})
										);
									})
								}
								outputMarkdown
							/>
							
							<Select name='identifier' label='Your name' initialValue='MEMBER' required>
								<option value='MEMBER'>{member?.names.member}</option>
								{member?.names.member !== member?.names.account && (
									<option value='ACCOUNT'>{member?.names.account}</option>
								)}
								<option value='FAMILY'>{member?.names.family}</option>
							</Select>
						</APIForm>
					</FormContainer>
				</Panel>
			</AnimateHeight>
			
			{allTopics.length === 0 && (
				<em>No topics have been posted yet. Create a new topic to start a conversation with other residents!</em>
			)}
			
			{allTopics.length > 0 && (
				<SearchAndPageSizeWrapper centerOnMobile grow={1} moreSpaceBetween>
					{searchFieldWithPlaceholder}
					{pageSizeField}
				</SearchAndPageSizeWrapper>
			)}
			
			{searchQuery.length > 0 && allTopics.length > 0 && (
				<React.Fragment>
					<ResultsHeading>Showing results for “{searchQuery}”</ResultsHeading>
					{filteredTopics?.length === 0 && <em>No results found…</em>}
				</React.Fragment>
			)}
			
			{filteredTopics.map((topic) => {
				// Parse and sanitize topic content
				const sanitizedContent = {
					__html: parseAndSanitizeMarkdown(topic.text),
				};
				
				
				// Generate topic route
				const topicLink = `topic/${encodeURIComponent(topic.title.replace(/\W+/g, ' ').replace(/ /g, '-'))}-${topic.id}`;
				
				
				// Return JSX
				return (
					<Topic key={topic.id}>
						<Title>
							<PostLink to={topicLink} $underlineOnHover>
								{topic.title}
							</PostLink>
						</Title>
						
						<Byline post={topic} created={topic.posted} action='posted' />
						
						<PostLink to={topicLink}>
							<TopicContent dangerouslySetInnerHTML={sanitizedContent} />
						</PostLink>
						
						<Comments to={topicLink} smaller color='gray'>
							<FontAwesomeIcon icon={faComment} />
							{topic.comments} comment{topic.comments === 1 ? '' : 's'}
						</Comments>
					</Topic>
				);
			})}
			
			{paginationComponent}
		</React.Fragment>
	);
};

export default Forum;
